import {
  Notice,
  signalVariants,
  Stack,
  Text,
  textVariants,
  vars,
  type SignalVariantTypes,
} from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import {
  ProviderBookingResult,
  RefundCaseCancelReason,
  RefundCaseStep,
  type RefundCaseStep as RefundCaseStepType,
} from '@eti/schema-types';
import { useCallback } from 'react';
import { getProviderBookings } from './utils';
import RefundStatusCard from './RefundStatusCard';
import RefundStatusSteps from './RefundStatusSteps';
import type { RefundCasesType, TransformedProviderBookingType } from './models';

const noticeDescriptionStyles = css`
  margin-inline-start: 28px;
`;

const noticeTitleStyles = css`
  color: ${vars.colors.text};
`;

interface RefundStatusCasesProps {
  cases: RefundCasesType;
}

const RefundStatusCases = ({ cases }: RefundStatusCasesProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const providerBookings = getProviderBookings(cases);

  const getRejectedTextKeyPrefix = (providerBookingResult: ProviderBookingResult) => {
    switch (providerBookingResult) {
      case ProviderBookingResult.RejectedAirlineCovid_19Policy:
        return 'MyBookings.RefundStatus.Rejected.AirlineCovid19Policy';
      case ProviderBookingResult.RejectedRefundLowerThanFee:
        return 'MyBookings.RefundStatus.Rejected.RefundLowerThanFee';
      case ProviderBookingResult.RejectedNoResponseFromAirline:
        return 'MyBookings.RefundStatus.Rejected.NoResponseFromAirline';
      case ProviderBookingResult.RejectedByAirline:
        return 'MyBookings.RefundStatus.Rejected.ByAirline';
      case ProviderBookingResult.RejectedAirlineDeclaredBankruptcy:
        return 'MyBookings.RefundStatus.Rejected.AirlineDeclaredBankruptcy';
      case ProviderBookingResult.RejectedAirlineDeclaredFinancialReconstruction:
        return 'MyBookings.RefundStatus.Rejected.AirlineDeclaredReconstruction';
      case ProviderBookingResult.RejectedAirlineRefundedCustomerDirectly:
        return 'MyBookings.RefundStatus.Rejected.AirlineRefundedCustomerDirectly';
      case ProviderBookingResult.RejectedAirlineSuspended:
        return 'MyBookings.RefundStatus.Rejected.AirlineSuspended';
      default:
        return null;
    }
  };

  const getProviderBookingContent = useCallback(
    (
      providerBooking: TransformedProviderBookingType,
      isRyanairProviderBooking: boolean,
    ): {
      badge: { variant?: SignalVariantTypes; title: string };
      iconVariant?: SignalVariantTypes;
      title?: string;
      description?: string;
      dataTestId?: string;
    } | null => {
      if (providerBooking.__typename === 'RefundCaseProviderBooking') {
        const { cancelReason, carrier, carrierCode, providerBookingResult, reference, steps } =
          providerBooking;
        const activeStep = steps.find((step) => !step.completed);

        if (providerBookingResult) {
          // SUCCESS
          if (providerBookingResult === ProviderBookingResult.Success) {
            let title = t('MyBookings.RefundStatus.Completed.Title');
            let description = t('MyBookings.RefundStatus.Completed.Description', carrier);

            if (cancelReason === RefundCaseCancelReason.OrphanedFunds) {
              title = t('MyBookings.RefundStatus.MoneyPayedOutToCustomer.OrphanFunds.Name');
              description = t(
                'MyBookings.RefundStatus.MoneyPayedOutToCustomer.OrphanFunds.Success.Text',
              );
            }

            if (
              cancelReason === RefundCaseCancelReason.ScheduleChangeRefund &&
              isRyanairProviderBooking
            ) {
              description = t('MyBookings.RefundStatus.ScheduleChangeRefund.Completed.Description');
            }

            return {
              badge: {
                variant: signalVariants.POSITIVE,
                title: t('MyBookings.RefundStatus.BadgeStatus.Completed'),
              },
              iconVariant: signalVariants.POSITIVE,
              title,
              description,
              dataTestId: `refund-status-${reference}-confirmation-message`,
            };
          }

          // REJECTED
          const prefix = getRejectedTextKeyPrefix(providerBookingResult);
          if (prefix !== null) {
            return {
              badge: {
                variant: signalVariants.CRITICAL,
                title: t('MyBookings.RefundStatus.BadgeStatus.Rejected'),
              },
              iconVariant: signalVariants.CRITICAL,
              title: t(`${prefix}.Name`, carrier),
              description: t(`${prefix}.Description`, carrier),
              dataTestId: `refund-status-${reference}-critical-message-${providerBookingResult}`,
            };
          }
        } else {
          // CARRIERS UNDER FINANCIAL RECONSTRUCTION
          const carrierListUnderFinancialReconstruction = p(
            'IbeClient.MyBookings.RefundStatus.CarriersUnderFinancialReconstruction',
          );
          if (
            !providerBooking.cc2Carrier &&
            activeStep &&
            carrierListUnderFinancialReconstruction.includes(providerBooking.carrierCode) &&
            (
              [
                RefundCaseStep.RefundRequestReceivedFromCustomer,
                RefundCaseStep.CustomerRequestedRefundFromProvider,
                RefundCaseStep.RequestedRefundFromProvider,
              ] as RefundCaseStepType[]
            ).includes(activeStep.step)
          ) {
            return {
              badge: {
                variant: signalVariants.CRITICAL,
                title: t('MyBookings.RefundStatus.BadgeStatus.Rejected'),
              },
              iconVariant: signalVariants.CRITICAL,
              title: t('MyBookings.RefundStatus.CarrierUnderFinancialReconstruction.Title'),
              description: t(
                `MyBookings.RefundStatus.CarrierUnderFinancialReconstruction.Description.${carrierCode}`,
              ),
              dataTestId: `refund-status-${reference}-carrier-${carrierCode}-under-financial-reconstruction-message`,
            };
          }

          // PROVIDER BOOKING STEPS - COMPLETED
          const isLastStepCompleted =
            providerBooking.steps[providerBooking.steps.length - 1].completed;
          if (isLastStepCompleted) {
            return {
              badge: {
                variant: signalVariants.POSITIVE,
                title: t('MyBookings.RefundStatus.BadgeStatus.Completed'),
              },
            };
          }

          // PROVIDER BOOKING STEPS - REQUIRES ACTION - IN PROGRESS
          const isCustomerActionRequired =
            activeStep?.step === RefundCaseStep.CustomerRequestedRefundFromProvider &&
            (cancelReason !== RefundCaseCancelReason.ScheduleChangeRefund ||
              !isRyanairProviderBooking);
          return {
            badge: {
              variant: signalVariants.CAUTION,
              title: isCustomerActionRequired
                ? t('MyBookings.RefundStatus.BadgeStatus.RequiresAction')
                : t('MyBookings.RefundStatus.BadgeStatus.InProgress'),
            },
          };
        }
      }

      // LINKED PNR
      if (providerBooking.__typename === 'RefundCaseRelatedProviderBooking') {
        return {
          badge: {
            title: t('MyBookings.RefundStatus.BadgeStatus.Unavailable'),
          },
          iconVariant: signalVariants.INFORMATION,
          title: t('MyBookings.RefundStatus.LinkedPNR.Title'),
          dataTestId: `refund-status-${providerBooking.reference}-linked-pnr-message`,
        };
      }

      return null;
    },
    [p, t],
  );

  return (
    <Stack data-testid="refund-status-cases">
      {providerBookings.length > 1 && (
        <Text>{t('MyBookings.RefundStatus.MultipleCases.Text')}</Text>
      )}
      <Stack role="list">
        {providerBookings.map((providerBooking: TransformedProviderBookingType, index: number) => {
          const ryanairCarriers = p('IbeClient.RyanairMarketingCarriers') || [];
          const isRyanairProviderBooking =
            providerBooking.__typename === 'RefundCaseProviderBooking' &&
            ryanairCarriers.some(
              (ryanairCarrierCode: string) =>
                ryanairCarrierCode.toLowerCase() === providerBooking.carrierCode.toLowerCase(),
            );
          const providerBookingContent = getProviderBookingContent(
            providerBooking,
            isRyanairProviderBooking,
          );

          if (!providerBookingContent) {
            return null;
          }

          return (
            <RefundStatusCard
              badge={providerBookingContent.badge}
              key={`${providerBooking.reference}-${index + 1}`}
              providerBooking={providerBooking}
            >
              <>
                {providerBookingContent.title && providerBookingContent.iconVariant ? (
                  <Stack data-testid={providerBookingContent.dataTestId} spacing={0}>
                    <Notice variant={providerBookingContent.iconVariant}>
                      <Text className={noticeTitleStyles} variant={textVariants._14_BOLD}>
                        {providerBookingContent.title}
                      </Text>
                    </Notice>
                    {providerBookingContent.description && (
                      <Text asHtml className={noticeDescriptionStyles}>
                        {providerBookingContent.description}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  providerBooking.__typename === 'RefundCaseProviderBooking' && (
                    <RefundStatusSteps
                      cancelReason={providerBooking.cancelReason}
                      carrier={providerBooking.carrier}
                      isCC2Carrier={providerBooking.cc2Carrier}
                      isRyanairProviderBooking={isRyanairProviderBooking}
                      steps={providerBooking.steps}
                    />
                  )
                )}
              </>
            </RefundStatusCard>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default RefundStatusCases;
